import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import useSWR from "swr";

import { useCallback, useEffect, useState } from "react";

import { twMerge } from "tailwind-merge";

import { Loader } from "components/customElements/loader";
import DiscussionSnippet from "components/DiscussionSnippet";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useInfiniteList from "hooks/useInfiniteList";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { resourceDiscussionsInclude } from "consts/included";

const DiscussionModal = dynamic(() => import("components/DiscussionModal"));
const DiscussionComposeModal = dynamic(
  () => import("components/DiscussionComposeModal")
);

type EntityPageDiscussionsListProps = {
  onEmpty?: () => void;
};

export const entityDiscussionsUrl = (slug: string, pageIndex: number) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}/comment-threads`);
  url.searchParams.append("include", resourceDiscussionsInclude);
  url.searchParams.append("page[size]", "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", "-updated-at");

  return url.href;
};

export const fetcher = (endpoint: string) => {
  return baseFetcher(endpoint, undefined, {
    // comments: {
    //   valueForRelationship(relationship: { id: string }) {
    //     return {
    //       id: relationship.id,
    //     };
    //   },
    // },
  });
};

const EntityPageDiscussionsList = ({
  onEmpty,
}: EntityPageDiscussionsListProps) => {
  const { account: loggedInAccount } = useAuthentication();

  const { data: phoneNumber, isValidating: isPhoneNumberValidating } = useSWR(
    loggedInAccount?.id
      ? `/api/v2/accounts/${loggedInAccount?.id}/phone-number`
      : null,
    baseFetcher
  );

  const isUnverifiedPreviewPass =
    loggedInAccount?.isPreviewPass &&
    !isPhoneNumberValidating &&
    !(phoneNumber?.verified || phoneNumber?.skipVerification);

  const router = useRouter();
  const slug = router.query.slug as string;
  const itemId = router.query.discussion as string;

  const { data: entity } = useApi(entityUrl(slug));

  const { infiniteList, mutate, prependItem, isEmpty } = useInfiniteList(
    (pageIndex) => entityDiscussionsUrl(slug, pageIndex + 1),
    {
      swrOptions: {
        revalidateOnMount: true,
        revalidateIfStale: true,
      },
      hasMore: ({ data }) =>
        !data?.length || !!data?.[data?.length - 1]?.data?.length,
      endIndicator: () => (
        <div className="text-center">No more discussion found</div>
      ),
      emptyIndicator: () => (
        <EmptyState
          type="discussion"
          entity={entity}
          showComposer={false}
          onDiscussionPost={() => mutate()}
        />
      ),
      loadingIndicator: () => <Loader numberOfItems={5} />,
      pageRender(pageData, _index) {
        if (!pageData) return null;

        return pageData?.data?.map?.((discussion: any) => (
          <DiscussionSnippet
            discussion={discussion}
            key={discussion.id}
            style={"compactV2"}
            showComposerByDefault
            noBorder
            hideReply
            onClick={() => {
              const parentId =
                discussion?.["parent-id"] || discussion?.parent?.id;
              router.query.discussion = parentId || discussion?.id;
              router.push(router, undefined, { shallow: true, scroll: false });
            }}
          />
        ));
      },
    }
  );

  useEffect(() => {
    if (isEmpty) onEmpty && onEmpty();
  }, [isEmpty, onEmpty]);

  const [showComposeModal, setShowComposeModal] = useState(false);

  const onCloseModal = useCallback(() => {
    const query = { ...router.query };
    delete query.discussion;

    router.replace({ query }, undefined, { shallow: true, scroll: false });
  }, [router]);

  return (
    <>
      <div className="">
        {loggedInAccount?.canPostComment &&
          !isEmpty &&
          !isUnverifiedPreviewPass && (
            <div className="flex md:justify-end mb-[15px]">
              <button
                className={twMerge(
                  "border-0 bg-smart-teal hover:bg-dark-teal",
                  "text-white text-[13px] font-bold leading-[1]",
                  "rounded-full px-[1.25em] py-[0.75em]"
                )}
                onClick={() => setShowComposeModal(true)}
              >
                Start New
              </button>
            </div>
          )}{" "}
        <div className="">{infiniteList}</div>
      </div>
      {showComposeModal && (
        <DiscussionComposeModal
          style="v3"
          commentable={entity}
          onClose={() => setShowComposeModal(false)}
          onDiscussionPost={(newDiscussion) => {
            if (newDiscussion) {
              prependItem(newDiscussion);
            } else {
              mutate();
            }
            setShowComposeModal(false);
          }}
        />
      )}
      {itemId && (
        <DiscussionModal id={itemId} onClose={onCloseModal} style="v2" />
      )}
    </>
  );
};

export default EntityPageDiscussionsList;
